import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectedVehicleCard, vehicleLabel } from '../components/inventory/SelectedVehicleCard';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowRight, ArrowLeft, Check, MessageCircle, Clock, Phone, ShieldCheck, Shield, Star, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { getTrafficAttribution } from '../components/Analytics';
import CallbackModal from '../components/CallbackModal';
import { ReferralSection } from '../components/ReferralSection';

const ROTATING_FR = ['une faillite', 'une proposition de consommateur', 'un refus bancaire', 'un nouveau départ au Canada', 'un crédit à rebâtir'];
const ROTATING_EN = ['a bankruptcy', 'a consumer proposal', 'a bank refusal', 'a fresh start in Canada', 'credit to rebuild'];

const RotatingWord = ({ language }) => {
  const words = language === 'en' ? ROTATING_EN : ROTATING_FR;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % words.length), 2600);
    return () => clearInterval(t);
  }, [words.length]);
  return (
    <span key={i} className="inline-block text-white font-semibold animate-word-in" data-testid="loan-rotating-word">
      {words[i]}
    </span>
  );
};
import LiveVisitorCount from '../components/LiveVisitorCount';
import ReviewCarousel from '../components/ReviewCarousel';

const API = '/api';

const LoanRequest = () => {
  const { language } = useLanguage();

  useEffect(() => {
    document.title = language === 'en' 
      ? 'Bad Credit Auto Financing | Credit Auto Reset' 
      : 'Financement Auto Mauvais Credit | Credit Auto Reset';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', language === 'en'
      ? 'Apply for auto financing even with bad credit. Free evaluation, no obligation, fast response. 2nd and 3rd chance financing in Quebec.'
      : 'Faites une demande de financement auto même avec mauvais crédit. Évaluation gratuite, sans obligation, réponse rapide. Financement 2e et 3e chance au Québec.');
  }, [language]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    vehicleType: '',
    budgetRange: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [emailError, setEmailError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    const vid = searchParams.get('vehicule');
    if (!vid) return;
    axios.get(`${API}/vehicles/inventory/${vid}`)
      .then((res) => { setSelectedVehicle(res.data); setCurrentStep(3); })
      .catch(() => setSelectedVehicle(null));
  }, [searchParams]);

  const removeSelectedVehicle = () => {
    setSelectedVehicle(null);
    setSearchParams({});
    setCurrentStep(1);
  };

  // SMS Chat Widget State
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [chatPhone, setChatPhone] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatSent, setChatSent] = useState(false);
  const [chatFormData, setChatFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    vehicleType: '',
    budgetRange: ''
  });

  const handleStartChat = async () => {
    // Validate all fields
    if (!chatFormData.firstName.trim() || !chatFormData.lastName.trim() || 
        !chatFormData.email.trim() || !chatPhone.trim() || 
        !chatFormData.vehicleType || !chatFormData.budgetRange) {
      toast({ 
        title: language === 'en' ? 'Please fill in all fields' : 'Veuillez remplir tous les champs', 
        variant: 'destructive' 
      });
      return;
    }

    // Format phone number
    let formattedPhone = chatPhone.replace(/\D/g, '');
    if (formattedPhone.length === 10) {
      formattedPhone = '+1' + formattedPhone;
    } else if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+' + formattedPhone;
    }

    // Build message with customer info
    const vehicleLabel = chatVehicleTypes.find(v => v.value === chatFormData.vehicleType)?.[language === 'en' ? 'labelEn' : 'labelFr'] || chatFormData.vehicleType;
    const budgetLabel = chatBudgetOptions.find(b => b.value === chatFormData.budgetRange)?.[language === 'en' ? 'labelEn' : 'labelFr'] || chatFormData.budgetRange;
    
    const fullMessage = `New Live Chat Request:\n` +
      `Name: ${chatFormData.firstName} ${chatFormData.lastName}\n` +
      `Email: ${chatFormData.email}\n` +
      `Phone: ${formattedPhone}\n` +
      `Vehicle: ${vehicleLabel}\n` +
      `Budget: ${budgetLabel}\n` +
      `Message: ${chatMessage || 'Customer wants to chat live'}`;

    setChatSending(true);
    try {
      await axios.post(`${API}/start-chat`, {
        customer_phone: formattedPhone,
        customer_name: `${chatFormData.firstName} ${chatFormData.lastName}`,
        customer_email: chatFormData.email,
        vehicle_type: chatFormData.vehicleType,
        budget_range: chatFormData.budgetRange,
        message: fullMessage,
        language: language
      });
      
      setChatSent(true);
      toast({ 
        title: language === 'en' ? 'Message sent! Check your phone for our SMS reply.' : 'Message envoyé! Vérifiez votre téléphone pour notre réponse par SMS.' 
      });
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to send message. Please try again.' : 'Échec de l\'envoi. Veuillez réessayer.', 
        variant: 'destructive' 
      });
    }
    setChatSending(false);
  };

  const resetChatWidget = () => {
    setShowChatWidget(false);
    setChatSent(false);
    setChatPhone('');
    setChatMessage('');
    setChatFormData({
      firstName: '',
      lastName: '',
      email: '',
      vehicleType: '',
      budgetRange: ''
    });
  };

  const chatVehicleTypes = [
    { value: 'sedan', labelEn: 'Auto', labelFr: 'Auto' },
    { value: 'suv', labelEn: 'SUV', labelFr: 'VUS' },
    { value: 'truck', labelEn: 'Truck', labelFr: 'Camion' }
  ];

  const chatBudgetOptions = [
    { value: 'under-350', labelEn: 'Less than $350/mo', labelFr: 'Moins de 350$/mois' },
    { value: '351-475', labelEn: '$351-$475/mo', labelFr: '351$-475$/mois' },
    { value: '476-600', labelEn: '$476-$600/mo', labelFr: '476$-600$/mois' },
    { value: '601-750', labelEn: '$601-$750/mo', labelFr: '601$-750$/mois' },
    { value: '751-900', labelEn: '$751-$900/mo', labelFr: '751$-900$/mois' },
    { value: '901-plus', labelEn: '$901+/mo', labelFr: '901$+/mois' }
  ];

  const vehicleTypes = [
    {
      value: 'sedan',
      labelEn: 'Auto',
      labelFr: 'Auto',
      image: '/images/vehicle-sedan.avif'
    },
    {
      value: 'suv',
      labelEn: 'SUV',
      labelFr: 'VUS',
      image: '/images/vehicle-suv.png'
    },
    {
      value: 'truck',
      labelEn: 'Truck',
      labelFr: 'Camion',
      image: '/images/vehicle-truck.webp'
    }
  ];

  const budgetOptions = [
    { value: 'under-350', labelEn: 'Less than $350/month', labelFr: 'Moins de 350 $/mois' },
    { value: '351-475', labelEn: '$351 - $475/month', labelFr: '351 $ - 475 $/mois' },
    { value: '476-600', labelEn: '$476 - $600/month', labelFr: '476 $ - 600 $/mois' },
    { value: '601-750', labelEn: '$601 - $750/month', labelFr: '601 $ - 750 $/mois' },
    { value: '751-900', labelEn: '$751 - $900/month', labelFr: '751 $ - 900 $/mois' },
    { value: '901-plus', labelEn: '$901+ /month', labelFr: '901 $ et plus/mois' }
  ];

  const handleVehicleSelect = (type) => {
    setFormData({ ...formData, vehicleType: type });
    // Auto-advance to next step after selection
    setTimeout(() => {
      setCurrentStep(2);
    }, 300);
  };

  const handleBudgetSelect = (budget) => {
    setFormData({ ...formData, budgetRange: budget });
    // Auto-advance to next step after selection
    setTimeout(() => {
      setCurrentStep(3);
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate email on change
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(value.length > 0 && !emailRegex.test(value));
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.vehicleType) {
      toast({ title: language === 'en' ? 'Please select a vehicle type' : 'Veuillez choisir un type de véhicule', variant: 'destructive' });
      return;
    }
    if (currentStep === 2 && !formData.budgetRange) {
      toast({ title: language === 'en' ? 'Please select a budget range' : 'Veuillez choisir une fourchette budgétaire', variant: 'destructive' });
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError(true);
      toast({
        title: language === 'en' ? 'Please enter a valid email address' : 'Veuillez entrer une adresse courriel valide',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get traffic attribution for conversion tracking
      const attribution = getTrafficAttribution();
      
      const submitData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        employmentStatus: 'employed',
        annualIncome: '0',
        vehicleType: selectedVehicle ? vehicleLabel(selectedVehicle, false) : formData.vehicleType,
        budgetRange: selectedVehicle ? (formData.budgetRange || 'Véhicule choisi en inventaire') : formData.budgetRange,
        selectedVehicleId: selectedVehicle?.id || null,
        selectedVehicleUrl: selectedVehicle ? `${window.location.origin}/nos-vehicules/${selectedVehicle.id}` : null,
        message: '',
        source: 'Website',
        traffic_source: attribution?.source || 'direct',
        traffic_medium: attribution?.medium || 'none',
        campaign: attribution?.campaign || null,
        keyword: attribution?.keyword || null,
        gclid: attribution?.gclid || null
      };
      
      await axios.post(`${API}/loan-requests`, submitData);
      navigate('/merci');
    } catch (error) {
      console.error('Form submission error:', error.response?.data);
      let errorMessage = language === 'en' ? 'Error submitting application. Please try again.' : 'Erreur lors de la soumission. Veuillez réessayer.';
      
      // Check for validation errors
      if (error.response?.data?.detail) {
        const details = error.response.data.detail;
        if (Array.isArray(details) && details.length > 0) {
          const field = details[0].loc?.[1];
          if (field === 'email') {
            errorMessage = language === 'en' ? 'Please enter a valid email address.' : 'Veuillez entrer une adresse courriel valide.';
          } else if (field === 'phone') {
            errorMessage = language === 'en' ? 'Please enter a valid phone number.' : 'Veuillez entrer un numéro de téléphone valide.';
          }
        }
      }
      
      toast({
        title: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b130e]">
      {/* Hero */}
      <div className="pt-28 md:pt-40 pb-8 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 hero-grid"></div>
          <div className="absolute -top-20 left-[10%] w-96 h-96 bg-[#1a5c3b] rounded-full blur-3xl opacity-40 animate-blob"></div>
          <div className="absolute top-10 right-[8%] w-80 h-80 bg-green-500 rounded-full blur-3xl opacity-15 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-72 bg-emerald-700 rounded-full blur-3xl opacity-25 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="hidden md:inline-flex items-center gap-2 text-xs font-bold text-green-400 uppercase tracking-[0.2em] mb-5 animate-fade-up">
            <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 animate-ping"></span><span className="relative inline-flex w-2 h-2 rounded-full bg-green-400"></span></span>
            {language === 'en' ? 'FREE EVALUATION' : 'ÉVALUATION GRATUITE'}
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-0 md:mb-5 leading-[1.1] animate-fade-up" data-testid="loan-hero-title" style={{ animationDelay: '0.1s' }}>
            {language === 'en' ? 'Check Your' : 'Vérifiez Vos'}
            <br />
            <span className="bg-gradient-to-r from-green-300 via-green-400 to-emerald-500 bg-clip-text text-transparent">{language === 'en' ? 'Financing Options' : 'Options de Financement'}</span>
          </h1>
          <p className="hidden md:block text-lg md:text-xl text-gray-400 mb-3 min-h-[3.5rem] md:min-h-[1.75rem] animate-fade-up" style={{ animationDelay: '0.2s' }}>
            {language === 'en' ? 'Even after ' : 'Même après '}<RotatingWord language={language} />
          </p>
          <p className="hidden md:block text-base md:text-lg text-gray-500 max-w-xl mx-auto mb-8 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            {language === 'en' 
              ? 'Fill out the form below — it takes 2 minutes. No credit check, no obligation.' 
              : 'Remplissez le formulaire ci-dessous — ça prend 2 minutes. Sans enquête de crédit, sans obligation.'}
          </p>

          {/* Animated floating badges */}
          <div className="hidden md:flex flex-wrap items-center justify-center gap-4 text-sm">
            <div className="bg-white/10 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-2 backdrop-blur-sm animate-float" style={{ animationDelay: '0s' }}>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-gray-300">{language === 'en' ? 'Response within 24h' : 'Réponse en 24h'}</span>
            </div>
            <div className="bg-white/10 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-2 backdrop-blur-sm animate-float" style={{ animationDelay: '0.7s' }}>
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-gray-300">{language === 'en' ? '100% confidential' : '100% confidentiel'}</span>
            </div>
            <div className="bg-white/10 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-2 backdrop-blur-sm animate-float" style={{ animationDelay: '1.4s' }}>
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-gray-300">4.9/5 Google</span>
            </div>
            <div className="bg-[#1a5c3b]/30 border border-[#1a5c3b]/30 rounded-full px-5 py-2.5 flex items-center gap-2 backdrop-blur-sm animate-float" style={{ animationDelay: '2.1s' }}>
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-300 font-semibold">{language === 'en' ? '3,000+ clients financed' : '3 000+ clients financés'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-[#f8faf7] rounded-t-[2rem] md:rounded-t-[2.5rem] pt-5 md:pt-10 pb-10 md:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* Form Card - 3 columns */}
          <div className="lg:col-span-3">
          <Card className="shadow-2xl shadow-[#1a5c3b]/10 border-0 rounded-2xl overflow-hidden -mt-2 animate-fade-up" style={{ animationDelay: '0.35s' }}>
            <div className="h-1.5 bg-slate-100" data-testid="loan-progress-bar">
              <div className="h-full glow-border transition-[width] duration-700 ease-out" style={{ width: `${(currentStep / 3) * 100}%` }}></div>
            </div>
            <CardContent className="p-5 md:p-10 bg-white">

                <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-3">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className="flex items-center">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                        currentStep === step ? 'bg-[#1a5c3b] text-white shadow-lg shadow-[#1a5c3b]/20' :
                        currentStep > step ? 'bg-[#1a5c3b] text-white' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {currentStep > step ? <Check className="w-4 h-4" /> : step}
                      </div>
                      <span className={`ml-2 text-xs font-bold hidden sm:inline uppercase tracking-wide ${
                        currentStep === step ? 'text-[#1a5c3b]' : 'text-slate-400'
                      }`}>
                        {step === 1 && (language === 'en' ? 'Vehicle' : 'Véhicule')}
                        {step === 2 && (language === 'en' ? 'Budget' : 'Budget')}
                        {step === 3 && (language === 'en' ? 'Contact' : 'Contact')}
                      </span>
                    </div>
                    {step < 3 && (
                      <div className={`w-10 h-0.5 rounded ${
                        currentStep > step ? 'bg-[#1a5c3b]' : 'bg-slate-200'
                      }`}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {currentStep === 1 && (
              <div className="space-y-4 animate-fade-up" key="step1">
                <h2 className="text-lg font-semibold text-gray-800 text-center mb-4">
                  {language === 'en' ? 'Choose a vehicle type' : 'Choisissez un type de véhicule'}
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {vehicleTypes.map((vehicle) => (
                    <button
                      key={vehicle.value}
                      onClick={() => handleVehicleSelect(vehicle.value)}
                      data-testid={`loan-vehicle-${vehicle.value}`}
                      className={`group relative overflow-hidden rounded-xl border-2 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-xl ${
                        formData.vehicleType === vehicle.value
                          ? 'border-[#1a5c3b] shadow-lg ring-2 ring-green-200 -translate-y-1'
                          : 'border-gray-200 hover:border-green-400'
                      }`}
                    >
                      {formData.vehicleType === vehicle.value && (
                        <span className="absolute top-2 right-2 z-10 w-6 h-6 bg-[#1a5c3b] rounded-full flex items-center justify-center animate-word-in">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </span>
                      )}
                      <div className="bg-gradient-to-b from-gray-50 to-green-50/60 h-28 flex items-end justify-center overflow-hidden pb-1">
                        <img
                          src={vehicle.image}
                          alt={language === 'en' ? vehicle.labelEn : vehicle.labelFr}
                          className={`transition-transform duration-500 group-hover:scale-110 group-hover:translate-x-1 ${
                            vehicle.value === 'suv' ? 'w-36 h-24 object-contain' : 
                            vehicle.value === 'truck' ? 'w-36 h-24 object-contain' : 
                            'w-32 h-20 object-contain'
                          }`}
                        />
                      </div>
                      <div className="bg-white p-2 border-t text-center">
                        <span className="text-gray-700 font-medium text-sm">
                          {language === 'en' ? vehicle.labelEn : vehicle.labelFr}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4 animate-fade-up" key="step2">
                <h2 className="text-lg font-semibold text-gray-800 text-center mb-4">
                  {language === 'en' ? 'What is your budget?' : 'Quel est votre budget ?'}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {budgetOptions.map((budget) => (
                    <button
                      key={budget.value}
                      onClick={() => handleBudgetSelect(budget.value)}
                      data-testid={`loan-budget-${budget.value}`}
                      className={`p-3 rounded-lg border-2 font-medium text-sm text-left transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 active:scale-95 ${
                        formData.budgetRange === budget.value
                          ? 'border-[#1a5c3b] bg-[#1a5c3b]/10 text-[#1a5c3b]'
                          : 'border-gray-200 hover:border-green-400 text-gray-700 bg-white'
                      }`}
                    >
                      {language === 'en' ? budget.labelEn : budget.labelFr}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6 animate-fade-up" key="step3">
                {selectedVehicle && <SelectedVehicleCard v={selectedVehicle} en={language === 'en'} onRemove={removeSelectedVehicle} />}
                <h2 className="text-xl font-semibold text-gray-800 text-center mb-8">
                  {language === 'en' ? 'Your Contact Information' : 'Vos coordonnées'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">{language === 'en' ? 'First Name' : 'Prénom'} *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">{language === 'en' ? 'Last Name' : 'Nom'} *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700">{language === 'en' ? 'Email' : 'Courriel'} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`h-12 text-base ${emailError ? 'border-red-500 border-2 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {emailError && (
                      <p className="text-red-500 text-xs mt-1">
                        {language === 'en' ? 'Please enter a valid email address' : 'Veuillez entrer une adresse courriel valide'}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">{language === 'en' ? 'Phone' : 'Téléphone'} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base"
                    />
                  </div>
                </div>
              </form>
            )}

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <Button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1 || !!selectedVehicle}
                data-testid="loan-back-btn"
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {language === 'en' ? 'Back' : 'Retour'}
              </Button>
              
              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  size="sm"
                  data-testid="loan-continue-btn"
                  className="group btn-shimmer bg-[#1a5c3b] hover:bg-[#165432] text-white"
                >
                  {language === 'en' ? 'Continue' : 'Continuer'}
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  size="sm"
                  className="bg-[#1a5c3b] hover:bg-[#165432] text-white"
                  data-testid="loan-submit-btn"
                >
                  {isSubmitting ? (language === 'en' ? 'Sending...' : 'Envoi...') : (language === 'en' ? 'Check My Eligibility Free' : 'Vérifier mon admissibilité gratuitement')}
                  <Check className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
          </div>

          {/* Visual Side Panel - 2 columns (desktop only) */}
          <div className="hidden lg:flex lg:col-span-2 flex-col gap-5 pt-4 animate-fade-up" style={{ animationDelay: '0.5s' }}>
            {/* Key stats */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-bold text-[#0f172a] text-lg mb-4">{language === 'en' ? 'Why apply here?' : 'Pourquoi faire votre demande ici?'}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#1a5c3b]/10 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-4 h-4 text-[#1a5c3b]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f172a] text-sm">{language === 'en' ? 'One application, 20+ lenders' : 'Une demande, 20+ prêteurs'}</p>
                    <p className="text-xs text-slate-400">{language === 'en' ? 'We compare for you' : 'On compare pour vous'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#1a5c3b]/10 rounded-lg flex items-center justify-center shrink-0">
                    <Shield className="w-4 h-4 text-[#1a5c3b]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f172a] text-sm">{language === 'en' ? 'No credit inquiry' : 'Sans enquête de crédit'}</p>
                    <p className="text-xs text-slate-400">{language === 'en' ? 'Until you approve' : 'Tant que vous n\'approuvez pas'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#1a5c3b]/10 rounded-lg flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-[#1a5c3b]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0f172a] text-sm">{language === 'en' ? 'Response in 24 hours' : 'Réponse en 24 heures'}</p>
                    <p className="text-xs text-slate-400">{language === 'en' ? 'Fast and reliable' : 'Rapide et fiable'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Callback quick link */}
            <div className="bg-[#0b130e] rounded-2xl p-5 flex items-center gap-4">
              <div className="w-11 h-11 bg-[#1a5c3b] rounded-xl flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-white text-sm">{language === 'en' ? 'Prefer a call?' : 'Vous préférez un appel?'}</p>
                <button onClick={() => setShowCallbackModal(true)} className="text-green-400 font-bold text-sm hover:underline" data-testid="loan-callback-button">
                  {language === 'en' ? 'Schedule a callback →' : 'Planifier un rappel →'}
                </button>
              </div>
            </div>

            {/* Google review */}
            <a href="https://g.page/r/CXxH1z5HJ2FLEBM/review" target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 shadow-sm hover:border-[#1a5c3b]/30 transition-colors">
              <div className="flex flex-col items-center shrink-0">
                <div className="flex items-center gap-0.5 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-2xl font-extrabold text-[#0f172a]">4.9</span>
              </div>
              <div>
                <p className="font-bold text-[#0f172a] text-sm">Google Reviews</p>
                <p className="text-xs text-slate-400">{language === 'en' ? 'Read what our clients say' : 'Lisez ce que nos clients disent'}</p>
              </div>
            </a>
          </div>

          </div>
        </div>
      </div>

      <div className="hidden md:block"><ReferralSection /></div>

      {/* Callback Modal */}
      <CallbackModal isOpen={showCallbackModal} onClose={() => setShowCallbackModal(false)} />
    </div>
  );
};

export default LoanRequest;
